import type { UpsellProductItem } from "@intelligems/ig-types/dtos/upsell-product-rules";

import { fromGqlId } from "@src/api/formatters/shopify";
import type {
  ProductPickerProduct,
  ProductPickerSelectedProduct,
} from "@src/store/slices/productPicker/types";

// Shared picker-selection -> canonical upsell conversions, used by both the
// checkout-block upsell action and the onsite-upsell config panel.

// These conversions read only a few fields, so any object with them works.
interface SelectedProductInput {
  id: string;
  title: string;
  image?: string | null;
  selectedVariantIds?: string[];
}

// A product with no selected variants means "all"; one with zero included
// variants is dropped.
export const selectionToUpsellProductItems = (
  selectedProducts: SelectedProductInput[],
  allProducts: ProductPickerProduct[]
): UpsellProductItem[] => {
  const productsMap = new Map(allProducts.map((p) => [p.id, p]));
  const items: UpsellProductItem[] = [];
  let order = 0;

  selectedProducts.forEach((selectedProduct) => {
    const selectedVariantIds = selectedProduct.selectedVariantIds ?? [];
    const noVariantsSpecified = selectedVariantIds.length === 0;
    const product = productsMap.get(selectedProduct.id);

    const variants = (product?.variants ?? []).map((variant) => ({
      variantId: fromGqlId(variant.id),
      variantTitle: variant.title,
      variantImage: variant.image ?? null,
      price: variant.price,
      compareAtPrice: variant.compareAtPrice,
      availableForSale: variant.availableForSale,
      inclusionType:
        noVariantsSpecified || selectedVariantIds.includes(variant.id)
          ? ("include" as const)
          : ("exclude" as const),
      variantOptions: variant.selectedOptions,
    }));

    if (!variants.some((v) => v.inclusionType === "include")) {
      return;
    }

    items.push({
      order: order++,
      productId: fromGqlId(selectedProduct.id),
      productTitle: product?.title || selectedProduct.title,
      productImage: product?.image ?? selectedProduct.image ?? null,
      options: product?.options,
      variants,
    });
  });

  return items;
};

// Seed picker "selected products" from a fetched product list keyed by id, so
// saved selections render before the live load reconciles. The variant fields
// differ by picker (PDP selects whole products, cart selects specific
// variants), so callers supply them per product.
export const fetchedToSelectedProducts = (
  products: ProductPickerProduct[],
  ids: string[],
  variantSelection: (
    product: ProductPickerProduct | undefined,
    id: string
  ) => Pick<
    ProductPickerSelectedProduct,
    "selectedVariantIds" | "totalVariantCount"
  >
): ProductPickerSelectedProduct[] => {
  const byId = new Map(products.map((p) => [p.id, p]));
  return ids.map((id) => {
    const product = byId.get(id);
    return {
      id,
      title: product?.title ?? "",
      image: product?.image,
      handle: product?.handle ?? "",
      requiresSellingPlan: product?.requiresSellingPlan,
      ...variantSelection(product, id),
    };
  });
};

// Product-level ids for the PDP "specific products" condition.
export const selectionToProductIds = (
  selectedProducts: SelectedProductInput[]
): string[] => selectedProducts.map((p) => fromGqlId(p.id));

// Flat {productId, variantId} pairs for a cart "contains variants" condition:
// specific picks use their ids directly; whole-product picks enumerate variants.
export const selectionToCartVariantPairs = (
  selectedProducts: SelectedProductInput[],
  allProducts: ProductPickerProduct[]
): { productId: string; variantId: string }[] => {
  const productsMap = new Map(allProducts.map((p) => [p.id, p]));
  const pairs: { productId: string; variantId: string }[] = [];

  selectedProducts.forEach((selected) => {
    const productId = fromGqlId(selected.id);
    if (selected.selectedVariantIds === undefined) {
      for (const variant of productsMap.get(selected.id)?.variants ?? []) {
        pairs.push({ productId, variantId: fromGqlId(variant.id) });
      }
    } else {
      for (const variantId of selected.selectedVariantIds) {
        pairs.push({ productId, variantId: fromGqlId(variantId) });
      }
    }
  });

  return pairs;
};
